import { verificationFormSteps } from 'features/customerPanel/accountVerification/component/verificationSteps';
import { VerificationFormData } from 'features/customerPanel/accountVerification/interface/verification';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import FormContainer from 'shared/components/dialog/FormContainer';
import { APP_URL } from 'shared/constants/common';

import { verificationEventsBack, verificationEventsForwards } from 'features/customerPanel/accountVerification/utility/utility';
import WebView from 'features/webView/utils';
import { Form, Formik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import Button from 'shared/components/form/button';
import FormButtons from 'shared/components/form/formButtons';
import { SectionNavigation } from 'shared/constants/common';
import { sendEventsToDataLayer } from 'shared/constants/datalayer';
import { getVerificationFieldValidators } from 'shared/constants/validation-schema';
import { useNewVerificationPanelFlag } from 'shared/featureFlags';
import useSectionNavigation from 'shared/hooks/useSectionNavigation';
import { isFieldValueValid, isMobile } from 'shared/util/utility';

interface Props {
	formData: VerificationFormData;
	initialValues: VerificationFormData;
	onSubmit: (formData: VerificationFormData) => void;
	setFormData: (formData: VerificationFormData) => void;
	isDesktop: boolean;
	closeDialog?: () => void;
	loading: boolean;
	step: number;
	numberOfSteps: number;
}

const VerificationForm: FC<Props> = ({ step, numberOfSteps, initialValues, onSubmit, setFormData, closeDialog, loading }) => {
	const { changeNavigationView, goToRootView } = useSectionNavigation();
	const { t } = useTranslation();
	const history = useHistory();
	const fileUploadFront = useRef<HTMLInputElement>();
	const fileUploadBack = useRef<HTMLInputElement>();
	const heading = `verStep${step}Heading`;
	const subHeading = `verStep${step}Description`;
	const newVerificationPanelFlag = useNewVerificationPanelFlag();

	const handleNextStep = (formData: any) => {
		setFormData(formData);
		if (step === numberOfSteps) {
			onSubmit({ ...formData });
		} else {
			changeNavigationView(SectionNavigation.ACCOUNT_VERIFICATION, { stepId: step + 1 });
		}
	};

	const handlePrevStep = (formData: any) => {
		setFormData(formData);
		if (step === 1) {
			closeDialog?.();
			goToRootView();
			if (isMobile() && !newVerificationPanelFlag?.value) {
				history.push(APP_URL.home);
			}
		} else {
			changeNavigationView(SectionNavigation.ACCOUNT_VERIFICATION, { stepId: step - 1 });
		}
	};

	return (
		<FormContainer
			t={t}
			currentStep={step}
			numberOfSteps={numberOfSteps}
			isDesktop={!isMobile()}
			closeDialog={closeDialog}
			textualContent={{ heading, subHeading }}
		>
			<>
				<Formik
					enableReinitialize={true}
					initialValues={initialValues}
					validationSchema={getVerificationFieldValidators(step)}
					validateOnBlur={false}
					validateOnChange={true}
					onSubmit={onSubmit}
				>
					{({ handleBlur, setFieldValue, values, setValues, errors, touched, isValid, setFieldTouched, setFieldError, validateForm }) => {
						const handleNextStepChange = () => {
							validateForm().then((errors) => {
								if (isEmpty(errors)) {
									handleNextStep(values);
									sendEventsToDataLayer(verificationEventsForwards('manual'), step);
									WebView.broadcastAnalytics('VERIFICATION_COMPLETED');
								}
								return;
							});
						};
						const handlePrevStepChange = () => {
							handlePrevStep(values);
							sendEventsToDataLayer(verificationEventsBack(values.documentType, 'manual'), step);
						};

						return (
							<Form
								onSubmit={(e) => {
									e.preventDefault();
									handleNextStepChange();
								}}
							>
								<div>
									{verificationFormSteps.map(
										(VerificationForm, index) =>
											step === index + 1 && (
												<VerificationForm
													t={t}
													values={values}
													setValues={setValues}
													errors={errors}
													touched={touched}
													fileUploadFront={fileUploadFront}
													fileUploadBack={fileUploadBack}
													isFieldValueValid={isFieldValueValid}
													setFieldError={setFieldError}
													setFieldTouched={setFieldTouched}
													handleBlur={handleBlur}
													setFieldValue={setFieldValue}
												/>
											),
									)}
								</div>
								<FormButtons>
									<Button
										onClick={() => {
											handlePrevStepChange();
										}}
										type="button"
										btnType="outline-white"
										cyTestRef="verification-back"
										style={{ border: 'none' }}
									>
										{step === 1 ? t('quit') : t('back')}
									</Button>
									<Button type="submit" loading={loading} cyTestRef="verification-forward">
										{step === numberOfSteps ? t('finish') : t('further')}
									</Button>
								</FormButtons>
							</Form>
						);
					}}
				</Formik>
			</>
		</FormContainer>
	);
};

export default VerificationForm;
